import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { getUser } from '../utils/auth';
import { 
  BookOpen, Plus, Search, Filter, Edit, Trash2, Eye, 
  ChevronDown, X, Save, FileText, Layers, CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';

const SchoolQuestionBank = () => {
  const navigate = useNavigate();
  const user = getUser();
  const [stats, setStats] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  
  // Filter state
  const [filters, setFilters] = useState({
    subject_id: '',
    question_type: '',
    difficulty_level: '',
    topic: '',
    search: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;
  
  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [viewingQuestion, setViewingQuestion] = useState(null);

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    fetchQuestions();
  }, [filters, page]);

  const fetchInitialData = async () => {
    try {
      const [statsRes, subjectsRes] = await Promise.all([
        api.get('/school-questions/stats'),
        api.get('/school-questions/subjects')
      ]);
      setStats(statsRes.data);
      setSubjects(subjectsRes.data.subjects || []);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchQuestions = async () => {
    setLoadingQuestions(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      params.append('page', page);
      params.append('limit', limit);
      
      const res = await api.get(`/school-questions/questions?${params}`);
      setQuestions(res.data.questions || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error('Failed to load questions');
    } finally {
      setLoadingQuestions(false);
    }
  };

  const handleDelete = async (questionId) => {
    if (!window.confirm('Are you sure you want to delete this question?')) return;
    
    try {
      await api.delete(`/school-questions/questions/${questionId}`);
      toast.success('Question deleted');
      fetchQuestions();
      fetchInitialData();
    } catch (err) {
      toast.error('Failed to delete question');
    }
  };

  const getTypeColor = (type) => {
    return type === 'mcq' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700';
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty?.toLowerCase()) {
      case 'easy': return 'bg-green-100 text-green-700';
      case 'medium': return 'bg-yellow-100 text-yellow-700';
      case 'hard': return 'bg-red-100 text-red-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <BookOpen className="w-8 h-8 text-indigo-600" />
              <div>
                <h1 className="text-xl font-bold text-slate-900">Question Bank</h1>
                <p className="text-xs text-slate-500">Save and reuse questions</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(-1)}
                className="px-4 py-2 text-slate-600 hover:text-slate-900"
              >
                Back
              </button>
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                data-testid="add-question-btn"
              >
                <Plus className="w-4 h-4" />
                Add Question
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl p-4 border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <FileText className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats?.total_questions || 0}</p>
                <p className="text-sm text-slate-500">Total Questions</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-4 border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats?.mcq_count || 0}</p>
                <p className="text-sm text-slate-500">MCQ</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-4 border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Layers className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats?.structured_count || 0}</p>
                <p className="text-sm text-slate-500">Structured</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-4 border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <BookOpen className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats?.subjects_count || 0}</p>
                <p className="text-sm text-slate-500">Subjects</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-slate-200 mb-6">
          <div className="p-4 border-b border-slate-200">
            <div className="flex items-center gap-4">
              {/* Search */}
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search questions..."
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  data-testid="search-input"
                />
              </div>
              
              {/* Subject Filter */}
              <select
                value={filters.subject_id}
                onChange={(e) => setFilters(prev => ({ ...prev, subject_id: e.target.value }))}
                className="px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
                data-testid="subject-filter"
              >
                <option value="">All Subjects</option>
                {subjects.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
              
              {/* Type Filter */}
              <select
                value={filters.question_type}
                onChange={(e) => setFilters(prev => ({ ...prev, question_type: e.target.value }))}
                className="px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
                data-testid="type-filter"
              >
                <option value="">All Types</option>
                <option value="mcq">MCQ</option>
                <option value="structured">Structured</option>
              </select>
              
              {/* Difficulty Filter */}
              <select
                value={filters.difficulty_level}
                onChange={(e) => setFilters(prev => ({ ...prev, difficulty_level: e.target.value }))}
                className="px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
                data-testid="difficulty-filter"
              >
                <option value="">All Difficulties</option>
                <option value="Easy">Easy</option>
                <option value="Medium">Medium</option>
                <option value="Hard">Hard</option>
              </select>
              
              {/* Clear Filters */}
              {(filters.search || filters.subject_id || filters.question_type || filters.difficulty_level) && (
                <button
                  onClick={() => setFilters({ subject_id: '', question_type: '', difficulty_level: '', topic: '', search: '' })}
                  className="px-3 py-2 text-slate-600 hover:text-slate-900 flex items-center gap-1"
                >
                  <X className="w-4 h-4" />
                  Clear
                </button>
              )}
            </div>
          </div>
          
          {/* Questions List */}
          <div className="divide-y divide-slate-200">
            {loadingQuestions ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
              </div>
            ) : questions.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p className="font-medium">No questions found</p>
                <p className="text-sm">Add your first question to get started</p>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 inline-flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Question
                </button>
              </div>
            ) : (
              questions.map((q) => (
                <div key={q.id} className="p-4 hover:bg-slate-50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded ${getTypeColor(q.question_type)}`}>
                          {q.question_type === 'mcq' ? 'MCQ' : 'Structured'}
                        </span>
                        {q.subject_name && (
                          <span className="px-2 py-0.5 text-xs font-medium rounded bg-slate-100 text-slate-700">
                            {q.subject_name}
                          </span>
                        )}
                        {q.difficulty_level && (
                          <span className={`px-2 py-0.5 text-xs font-medium rounded ${getDifficultyColor(q.difficulty_level)}`}>
                            {q.difficulty_level}
                          </span>
                        )}
                        <span className="text-xs text-slate-500">
                          {q.total_marks} marks
                        </span>
                      </div>
                      
                      <p className="text-slate-900 line-clamp-2">
                        {q.question_text}
                      </p>
                      
                      {q.topic && (
                        <p className="text-xs text-slate-500 mt-1">Topic: {q.topic}</p>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2 ml-4">
                      <button
                        onClick={() => setViewingQuestion(q)}
                        className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg"
                        title="View"
                        data-testid={`view-${q.id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setEditingQuestion(q)}
                        className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg"
                        title="Edit"
                        data-testid={`edit-${q.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(q.id)}
                        className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                        title="Delete"
                        data-testid={`delete-${q.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          
          {/* Pagination */}
          {total > limit && (
            <div className="p-4 border-t border-slate-200 flex items-center justify-between">
              <p className="text-sm text-slate-500">
                Showing {(page - 1) * limit + 1} - {Math.min(page * limit, total)} of {total}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1 border border-slate-200 rounded disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(p => p + 1)}
                  disabled={page * limit >= total}
                  className="px-3 py-1 border border-slate-200 rounded disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Create/Edit Modal */}
      {(showCreateModal || editingQuestion) && (
        <QuestionFormModal
          question={editingQuestion}
          subjects={subjects}
          onClose={() => {
            setShowCreateModal(false);
            setEditingQuestion(null);
          }}
          onSave={() => {
            setShowCreateModal(false);
            setEditingQuestion(null);
            fetchQuestions();
            fetchInitialData();
          }}
        />
      )}

      {/* View Modal */}
      {viewingQuestion && (
        <QuestionViewModal
          question={viewingQuestion}
          onClose={() => setViewingQuestion(null)}
        />
      )}
    </div>
  );
};

// Question Form Modal Component
const QuestionFormModal = ({ question, subjects, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    subject_id: question?.subject_id || '',
    question_type: question?.question_type || 'structured',
    question_text: question?.question_text || '',
    total_marks: question?.total_marks || 0,
    difficulty_level: question?.difficulty_level || 'Medium',
    topic: question?.topic || '',
    mcq_options: question?.mcq_options || [
      { letter: 'A', text: '', is_correct: false },
      { letter: 'B', text: '', is_correct: false },
      { letter: 'C', text: '', is_correct: false },
      { letter: 'D', text: '', is_correct: false }
    ],
    correct_option: question?.correct_option || 0,
    parts: question?.parts || []
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.subject_id) {
      toast.error('Please select a subject');
      return;
    }
    if (!formData.question_text.trim()) {
      toast.error('Please enter question text');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        total_marks: parseInt(formData.total_marks) || (formData.question_type === 'mcq' ? 1 : 0)
      };

      if (formData.question_type !== 'mcq') {
        delete payload.mcq_options;
        delete payload.correct_option;
      }

      if (question) {
        await api.put(`/school-questions/questions/${question.id}`, payload);
        toast.success('Question updated');
      } else {
        await api.post('/school-questions/questions', payload);
        toast.success('Question created');
      }
      onSave();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save question');
    } finally {
      setSaving(false);
    }
  };

  const handleMCQOptionChange = (index, field, value) => {
    const newOptions = [...formData.mcq_options];
    newOptions[index] = { ...newOptions[index], [field]: value };
    
    // If marking as correct, unmark others
    if (field === 'is_correct' && value) {
      newOptions.forEach((opt, i) => {
        if (i !== index) opt.is_correct = false;
      });
      setFormData(prev => ({ ...prev, mcq_options: newOptions, correct_option: index }));
    } else {
      setFormData(prev => ({ ...prev, mcq_options: newOptions }));
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-200 sticky top-0 bg-white">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-slate-900">
              {question ? 'Edit Question' : 'Add New Question'}
            </h2>
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Subject */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Subject *</label>
            <select
              value={formData.subject_id}
              onChange={(e) => setFormData(prev => ({ ...prev, subject_id: e.target.value }))}
              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
              required
              data-testid="subject-select"
            >
              <option value="">Select Subject</option>
              {subjects.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>

          {/* Question Type */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Question Type</label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="question_type"
                  value="structured"
                  checked={formData.question_type === 'structured'}
                  onChange={(e) => setFormData(prev => ({ ...prev, question_type: e.target.value }))}
                  className="text-indigo-600"
                />
                <span>Structured</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="question_type"
                  value="mcq"
                  checked={formData.question_type === 'mcq'}
                  onChange={(e) => setFormData(prev => ({ ...prev, question_type: e.target.value }))}
                  className="text-indigo-600"
                />
                <span>MCQ</span>
              </label>
            </div>
          </div>

          {/* Question Text */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Question Text *</label>
            <textarea
              value={formData.question_text}
              onChange={(e) => setFormData(prev => ({ ...prev, question_text: e.target.value }))}
              rows={4}
              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
              placeholder="Enter the question..."
              required
              data-testid="question-text"
            />
          </div>

          {/* MCQ Options */}
          {formData.question_type === 'mcq' && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Options</label>
              <div className="space-y-3">
                {formData.mcq_options.map((opt, index) => (
                  <div key={opt.letter} className="flex items-center gap-3">
                    <span className="w-8 h-8 flex items-center justify-center bg-slate-100 rounded-lg font-medium">
                      {opt.letter}
                    </span>
                    <input
                      type="text"
                      value={opt.text}
                      onChange={(e) => handleMCQOptionChange(index, 'text', e.target.value)}
                      className="flex-1 px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      placeholder={`Option ${opt.letter}`}
                    />
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={opt.is_correct}
                        onChange={(e) => handleMCQOptionChange(index, 'is_correct', e.target.checked)}
                        className="text-green-600"
                      />
                      <span className="text-sm text-slate-600">Correct</span>
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Marks, Difficulty, Topic */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Marks</label>
              <input
                type="number"
                value={formData.total_marks}
                onChange={(e) => setFormData(prev => ({ ...prev, total_marks: e.target.value }))}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
                min="0"
                data-testid="marks-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Difficulty</label>
              <select
                value={formData.difficulty_level}
                onChange={(e) => setFormData(prev => ({ ...prev, difficulty_level: e.target.value }))}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
              >
                <option value="Easy">Easy</option>
                <option value="Medium">Medium</option>
                <option value="Hard">Hard</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Topic</label>
              <input
                type="text"
                value={formData.topic}
                onChange={(e) => setFormData(prev => ({ ...prev, topic: e.target.value }))}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
                placeholder="e.g., Mechanics"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-slate-600 hover:text-slate-900"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
              data-testid="save-question-btn"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {question ? 'Update Question' : 'Save Question'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Question View Modal
const QuestionViewModal = ({ question, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-200 sticky top-0 bg-white">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-slate-900">View Question</h2>
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {/* Metadata */}
          <div className="flex flex-wrap gap-2">
            <span className={`px-2 py-1 text-xs font-medium rounded ${question.question_type === 'mcq' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
              {question.question_type === 'mcq' ? 'MCQ' : 'Structured'}
            </span>
            {question.subject_name && (
              <span className="px-2 py-1 text-xs font-medium rounded bg-slate-100 text-slate-700">
                {question.subject_name}
              </span>
            )}
            {question.difficulty_level && (
              <span className="px-2 py-1 text-xs font-medium rounded bg-yellow-100 text-yellow-700">
                {question.difficulty_level}
              </span>
            )}
            <span className="px-2 py-1 text-xs font-medium rounded bg-green-100 text-green-700">
              {question.total_marks} marks
            </span>
          </div>

          {/* Question Text */}
          <div>
            <h3 className="text-sm font-medium text-slate-500 mb-2">Question</h3>
            <p className="text-slate-900 whitespace-pre-wrap">{question.question_text}</p>
          </div>

          {/* MCQ Options */}
          {question.question_type === 'mcq' && question.mcq_options && (
            <div>
              <h3 className="text-sm font-medium text-slate-500 mb-2">Options</h3>
              <div className="space-y-2">
                {question.mcq_options.map((opt, index) => (
                  <div
                    key={opt.letter || `opt-${index}`}
                    className={`p-3 rounded-lg border ${opt.is_correct ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'}`}
                  >
                    <span className="font-medium mr-2">{opt.letter}.</span>
                    <span>{opt.text}</span>
                    {opt.is_correct && (
                      <span className="ml-2 text-green-600 text-sm">(Correct)</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Topic */}
          {question.topic && (
            <div>
              <h3 className="text-sm font-medium text-slate-500 mb-1">Topic</h3>
              <p className="text-slate-700">{question.topic}</p>
            </div>
          )}

          {/* Created by */}
          <div className="text-sm text-slate-500 pt-4 border-t border-slate-200">
            Created by {question.created_by_name || 'Unknown'} on{' '}
            {question.created_at ? new Date(question.created_at).toLocaleDateString() : 'Unknown date'}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SchoolQuestionBank;
